// MealEntryForm — a meal is its own journal entry (REQUIREMENTS_NOTEBOOK ME-1, ME-2, ME-5; BOLUS_NOTES-74).
// 2026-10-12 (ME-6): a photo is sent at once to the home-lab vision model, which lists every item with a
// portion, carbs and its rationale; the user corrects anything, and the total fills the meal's carbs.
import React, { useEffect, useState } from 'react';
import { bolusnotesApi } from '../../services/api';
import { analysisTotal, CarbAnalysis, CarbItem } from '../../pages/notebookLayout';

interface MealEntryFormProps {
  onSave: (entryData: any) => Promise<void>;
  onCancel: () => void;
  initialData?: any;
  /** The meal's current photo (base64 or data URL), loaded by the page when editing. */
  photoData?: string | null;
  /** Test seam: the analysis call. */
  analyze?: (photo: string, hint?: string) => Promise<any>;
}

export const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snack', 'meal'];
const MAX_PHOTO_PX = 1568; // what vision models use; larger is scaled down by them

/** Shrink a picked photo to at most MAX_PHOTO_PX on its long side, as a JPEG data URL. */
function shrink(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Not an image'));
      img.onload = () => {
        const scale = Math.min(1, MAX_PHOTO_PX / Math.max(img.width, img.height));
        const c = document.createElement('canvas');
        c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
        c.getContext('2d')?.drawImage(img, 0, 0, c.width, c.height);
        resolve(c.toDataURL('image/jpeg', 0.8));
      };
      img.src = String(reader.result);
    };
    reader.readAsDataURL(file);
  });
}

const asDataUrl = (p?: string | null) => (!p ? null : p.startsWith('data:') ? p : `data:image/jpeg;base64,${p}`);
const errorText = (e: any) => {
  const m = e?.response?.data?.message || e?.message || '';
  return String(m).replace(/^[A-Z][A-Z_]{2,40}:\s*/, '') || 'Carb analysis failed. Enter the carbs by hand.';
};

const MealEntryForm: React.FC<MealEntryFormProps> = ({ onSave, onCancel, initialData, photoData, analyze }) => {
  const [mealType, setMealType] = useState<string>(initialData?.meal_type || 'meal');
  const [description, setDescription] = useState<string>(initialData?.description || '');
  const [carbs, setCarbs] = useState<string>(initialData?.carbs != null ? String(initialData.carbs) : '');
  const [photo, setPhoto] = useState<string | null>(asDataUrl(photoData));
  const [newPhoto, setNewPhoto] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<CarbAnalysis | null>(initialData?.carb_analysis || null);
  const [analyzing, setAnalyzing] = useState(false);
  const [hint, setHint] = useState('');
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => { if (!newPhoto) setPhoto(asDataUrl(photoData)); }, [photoData, newPhoto]);

  const runAnalysis = async (dataUrl: string) => {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) { setAnalysisError('No connection — enter the carbs by hand.'); return; }
    setAnalyzing(true); setAnalysisError(null);
    try {
      const r = await (analyze || bolusnotesApi.journal.analyzeMealPhoto)(dataUrl.replace(/^data:image\/\w+;base64,/, ''), hint || undefined);
      const a: CarbAnalysis = { items: (r?.items || []).map((i: any) => ({ ...i, edited: false })), confidence: r?.confidence, notes: r?.notes, model: r?.model, analyzed_at: r?.analyzed_at };
      setAnalysis(a);
      if (a.items.length) {
        setCarbs(String(analysisTotal(a)));
        if (!description.trim()) setDescription(a.items.map((i) => i.name).join(', '));
      } else setAnalysisError(a.notes || 'No food recognised — enter the carbs by hand.');
    } catch (e) {
      setAnalysisError(errorText(e));
    } finally { setAnalyzing(false); }
  };

  const pick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { const d = await shrink(f); setNewPhoto(d); setPhoto(d); await runAnalysis(d); } catch { setError('That file is not a photo.'); }
  };

  const setItems = (items: CarbItem[]) => {
    const a = { ...(analysis || { items: [] }), items };
    setAnalysis(a);
    setCarbs(String(analysisTotal(a)));
  };
  const editItem = (i: number, patch: Partial<CarbItem>) =>
    setItems((analysis?.items || []).map((it, k) => (k === i ? { ...it, ...patch, edited: true } : it)));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const c = carbs.trim() === '' ? null : Number(carbs);
    if (c != null && !(c >= 0)) { setError('Carbs must be a number.'); return; }
    if (!description.trim() && !(c && c > 0) && !photo) { setError('A meal needs a description, carbs or a photo.'); return; }
    setError(null);
    setIsSubmitting(true);
    try {
      // ME-5: only a NEW photo is sent; the server keeps the existing one otherwise.
      await onSave({
        meal_type: mealType, description: description.trim(), carbs: c,
        ...(newPhoto ? { photo_data: newPhoto } : {}),
        ...(analysis ? { carb_analysis: { ...analysis, total_carbs_g: analysisTotal(analysis) } } : {}),
      });
    } finally { setIsSubmitting(false); }
  };

  return (
    <div className="entry-form popup-form">
      <h3>{initialData ? 'Edit Meal' : 'New Meal'}</h3>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="meal-photo">Photo — the carbs are estimated from it</label>
          {photo && <img src={photo} alt="Meal" className="nb-meal-photo" />}
          <input id="meal-photo" type="file" accept="image/*" capture="environment" onChange={pick} />
          {newPhoto && !analyzing && (
            <div className="nb-analysis-again">
              <input type="text" placeholder={'Anything the photo can\'t show? (e.g. "ate half")'} value={hint} onChange={(e) => setHint(e.target.value)} />
              <button type="button" className="btn-secondary" onClick={() => runAnalysis(newPhoto)}>Analyze again</button>
            </div>
          )}
          {analyzing && <div className="nb-hint" role="status">Looking at the photo…</div>}
          {analysisError && <div className="nb-form-error" role="alert">{analysisError}</div>}
        </div>

        {analysis && analysis.items.length > 0 && (
          <div className="form-group nb-analysis" aria-label="What the photo shows">
            <label>What the photo shows <small>(estimates — check every item)</small></label>
            <table>
              <tbody>
                {analysis.items.map((it, i) => (
                  <tr key={i}>
                    <td>
                      <input aria-label={`Item ${i + 1}`} value={it.name} onChange={(e) => editItem(i, { name: e.target.value })} />
                      <div className="nb-hint">{[it.portion, it.rationale].filter(Boolean).join(' — ')}{it.edited ? ' · corrected' : ''}</div>
                    </td>
                    <td className="nb-analysis-g">
                      <input aria-label={`Carbs for item ${i + 1}`} type="number" min="0" step="1" value={it.carbs_g} onChange={(e) => editItem(i, { carbs_g: Number(e.target.value) || 0 })} />g
                    </td>
                    <td><button type="button" aria-label={`Remove item ${i + 1}`} onClick={() => setItems(analysis.items.filter((_, k) => k !== i))}>✕</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="nb-analysis-foot">
              <button type="button" className="btn-secondary" onClick={() => setItems([...analysis.items, { name: '', carbs_g: 0, rationale: 'added by hand', edited: true }])}>+ Add an item</button>
              <b>Total {analysisTotal(analysis)} g</b>
            </div>
            {(analysis.confidence || analysis.notes) && <div className="nb-hint">{[analysis.confidence && `Confidence: ${analysis.confidence}.`, analysis.notes].filter(Boolean).join(' ')}</div>}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="meal-type">Meal</label>
          <select id="meal-type" value={mealType} onChange={(e) => setMealType(e.target.value)}>
            {MEAL_TYPES.map((t) => <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="meal-desc">What was eaten</label>
          <textarea id="meal-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="e.g. pasta and salad" />
        </div>
        <div className="form-group">
          <label htmlFor="meal-carbs">Carbs (g)</label>
          <input id="meal-carbs" type="number" step="1" min="0" value={carbs} onChange={(e) => setCarbs(e.target.value)} placeholder="e.g. 60" />
        </div>
        {error && <div className="nb-form-error" role="alert">{error}</div>}
        <div className="form-actions" style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={isSubmitting}>Cancel</button>
          <button type="submit" className="btn-primary" disabled={isSubmitting || analyzing}>{isSubmitting ? 'Saving...' : 'Save Meal'}</button>
        </div>
      </form>
    </div>
  );
};

export default MealEntryForm;
